import React, { useState, useMemo } from "react";
import * as Yup from "yup";
import { useAdminApiClient } from "../hooks/useApiClient";
import { CherryHubAdminApi } from "../api/adminApiModels";
import { FormikErrors, FormikProvider, useFormik } from "formik";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Divider,
  HStack,
  IconButton,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tag,
  Text,
  VStack,
} from "@chakra-ui/react";
import { CloseIcon } from "@chakra-ui/icons";
import { cloneDeep } from "lodash";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  rectIntersection,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { SortableItem } from "../components/Dnd/SortableItem";
import { useRegistrationCustomFieldModal } from "../hooks/useRegistrationCustomFieldModal";
import {
  KvpFormField,
  getFormFieldId,
} from "./RegistrationFormConfigurationDetailForm";
import { TextField } from "../components/fields/TextField/TextField";
import { SwitchField } from "../components/fields/SwitchField/SwitchField";
import { JsonTextAreaField } from "../components/fields/TextArea/JsonTextAreaField";
import { HtmlTextArea } from "../components/fields/TextArea/HtmlTextArea";
import { IntegerField } from "../components/fields/IntegerField/IntegerField";
import {
  ChakraReactSelectField,
  SelectOption,
} from "../components/fields/SelectField/ChakraReactSelectField";
import { ApiResult } from "../api/dataApiClient";
import { useAdminApiRequest } from "../hooks/useApiRequest";
import { ServiceType } from "../enums/ServiceType";
import { SingleValue } from "react-select";

interface MemberSelfServiceConfigurationFormValues {
  friendlyName: string;
  isEnabled: boolean;
  integrationId: string | null;
  language: string;
  otpMethod: string;
  otpReturnPreferredMethodOnly: boolean;
  otpValidTimePeriodQuantity: number;
  otpValidTimePeriodUnit: string;
  pageTitle: string;
  logoFileUrl: string;
  headerText: string;
  footerText: string;
  privacyPolicyUrl: string;
  termsConditionsUrl: string;
  geoSuggestCountryFilter: string[];
  formFields: CherryHubAdminApi.CustomFormField[];
  themeData: string;
}

interface MemberSelfServiceConfigurationFormProps {
  businessId: string;
  configurationId?: string;
  configuration?: CherryHubAdminApi.MemberSelfServiceConfigurationUpdate | null;
  onCancel: () => void;
  onSuccess: (
    configuration: CherryHubAdminApi.MemberSelfServiceConfigurationUpdate
  ) => void;
  onFailure: (message?: string) => void;
}

const OTP_METHOD_OPTIONS = [
  { value: "PreferEmail", label: "Prefer Email" },
  { value: "PreferSMS", label: "Prefer SMS" },
];

const TIME_UNIT_OPTIONS = [
  { value: "Second", label: "Second" },
  { value: "Minute", label: "Minute" },
  { value: "Hour", label: "Hour" },
  { value: "Day", label: "Day" },
];

export const MemberSelfServiceConfigurationForm = ({
  businessId,
  configurationId,
  configuration,
  onCancel,
  onSuccess,
  onFailure,
}: MemberSelfServiceConfigurationFormProps) => {
  const apiClient = useAdminApiClient();

  const { data: integrationsData, isLoading: integrationsLoading } =
    useAdminApiRequest(
      (client) =>
        client.getBusinessIntegrations(businessId, {
          enabledService: ServiceType.MembershipManagementService,
        }),
      [businessId]
    );

  const integrationOptions = useMemo<SelectOption[]>(
    () =>
      (integrationsData?.Results ?? []).map((i) => ({
        value: i.Meta?.Id ?? null,
        label: i.DisplayName ?? i.Meta?.Id ?? "",
      })),
    [integrationsData]
  );

  const formik = useFormik<MemberSelfServiceConfigurationFormValues>({
    initialValues: {
      friendlyName: configuration?.FriendlyName ?? "",
      isEnabled: configuration?.IsEnabled ?? true,
      integrationId: configuration?.IntegrationId ?? null,
      language: configuration?.Language?.LanguageCode ?? "",
      otpMethod: configuration?.OTPMethod ?? "PreferEmail",
      otpReturnPreferredMethodOnly:
        configuration?.ReturnPreferredOTPMethodOnly ?? false,
      otpValidTimePeriodQuantity:
        configuration?.OTPValidTimePeriod?.Quantity ?? 5,
      otpValidTimePeriodUnit:
        configuration?.OTPValidTimePeriod?.Period ?? "Minute",
      pageTitle: configuration?.PageTitle ?? "",
      logoFileUrl: configuration?.LogoFileUrl ?? "",
      headerText: configuration?.HeaderText ?? "",
      footerText: configuration?.FooterText ?? "",
      privacyPolicyUrl: configuration?.PrivacyPolicyUrl ?? "",
      termsConditionsUrl: configuration?.TermsConditionsUrl ?? "",
      geoSuggestCountryFilter: configuration?.GeoSuggestCountryFilter ?? [],
      formFields: (
        (configuration?.FormFields as CherryHubAdminApi.CustomFormField[]) ?? []
      ).filter((f) => f.IsVisible !== false),
      themeData: configuration?.ThemeData
        ? JSON.stringify(configuration.ThemeData, null, 2)
        : "",
    },
    validationSchema: Yup.object().shape({
      friendlyName: Yup.string().required("Friendly Name is required"),
    }),
    validate: (val) => {
      const errors: FormikErrors<MemberSelfServiceConfigurationFormValues> = {};
      try {
        if (val.themeData) {
          JSON.parse(val.themeData);
        }
      } catch {
        errors.themeData = "Invalid JSON format";
      }
      return errors;
    },
    validateOnBlur: false,
    validateOnChange: true,
    onSubmit: async (val) => {
      let themeData: unknown = null;
      if (val.themeData) {
        try {
          themeData = JSON.parse(val.themeData);
        } catch {
          themeData = null;
        }
      }

      const payload: Partial<CherryHubAdminApi.MemberSelfServiceConfigurationUpdate> =
        {
          FriendlyName: val.friendlyName,
          IsEnabled: val.isEnabled,
          IntegrationId: val.integrationId ?? null,
          Language: val.language ? { LanguageCode: val.language } : undefined,
          OTPMethod:
            val.otpMethod as CherryHubAdminApi.MemberSelfServiceConfigurationUpdate["OTPMethod"],
          ReturnPreferredOTPMethodOnly: val.otpReturnPreferredMethodOnly,
          OTPValidTimePeriod: {
            Quantity: val.otpValidTimePeriodQuantity,
            Period:
              val.otpValidTimePeriodUnit as CherryHubAdminApi.TimePeriod["Period"],
          },
          PageTitle: val.pageTitle || null,
          LogoFileUrl: val.logoFileUrl || null,
          HeaderText: val.headerText || null,
          FooterText: val.footerText || null,
          PrivacyPolicyUrl: val.privacyPolicyUrl || null,
          TermsConditionsUrl: val.termsConditionsUrl || null,
          GeoSuggestCountryFilter:
            val.geoSuggestCountryFilter.length > 0
              ? val.geoSuggestCountryFilter
              : null,
          FormFields: val.formFields.length > 0 ? val.formFields : null,
          ThemeData: themeData,
        };

      let result: ApiResult<CherryHubAdminApi.MemberSelfServiceConfigurationUpdate> | null =
        null;

      if (configurationId) {
        result = await apiClient.editMemberSelfServiceConfiguration(
          businessId,
          configurationId,
          payload
        );
      } else {
        result = await apiClient.addMemberSelfServiceConfiguration(
          businessId,
          payload
        );
      }

      if (result.ok) {
        onSuccess(result.data);
      } else {
        onFailure(result.message);
      }
    },
  });

  const selectedIntegrationId = formik.values.integrationId;

  const { data: formFieldsData, isLoading: formFieldsLoading } =
    useAdminApiRequest(
      (client) => {
        if (!selectedIntegrationId) return null;
        return client.getMMSFormFields(businessId, selectedIntegrationId);
      },
      [businessId, selectedIntegrationId]
    );

  const kvpFormFields = useMemo<KvpFormField>(() => {
    if (!formFieldsData) return [];
    return Object.entries(formFieldsData).map(([key, value]) => ({
      key,
      value: (value as CherryHubAdminApi.CustomFormField[]).filter(
        (f) => f.IsUpdateable === true
      ),
    }));
  }, [formFieldsData]);

  const { modalElement: customFieldModalElement, showCustomFieldModal } =
    useRegistrationCustomFieldModal();

  const [activeFieldId, setActiveFieldId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = (event: any) => {
    setActiveFieldId(event.active.id as string);
  };

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const items = formik.values.formFields;
      const oldIndex = items.findIndex((f) => getFormFieldId(f) === active.id);
      const newIndex = items.findIndex((f) => getFormFieldId(f) === over.id);
      formik.setFieldValue("formFields", arrayMove(items, oldIndex, newIndex));
    }
    setActiveFieldId(null);
  };

  const addFields = () => {
    const results = cloneDeep(kvpFormFields);
    kvpFormFields.forEach((kvp, idxKvp) => {
      kvp.value.forEach((field) => {
        if (
          formik.values.formFields.find(
            (added) => getFormFieldId(added) === getFormFieldId(field)
          )
        ) {
          const resultsIdx = results[idxKvp].value.findIndex(
            (r) => getFormFieldId(r) === getFormFieldId(field)
          );
          if (resultsIdx !== -1) results[idxKvp].value.splice(resultsIdx, 1);
        }
      });
    });
    showCustomFieldModal(results.filter((kvp) => kvp.value.length)).then(
      ({
        ok,
        customFields,
      }: {
        ok: boolean;
        customFields?: CherryHubAdminApi.CustomFormField[];
      }) => {
        if (ok && customFields?.length) {
          formik.setFieldValue("formFields", [
            ...formik.values.formFields,
            ...customFields.map((f: CherryHubAdminApi.CustomFormField) => ({
              ...f,
              IsVisible: true,
            })),
          ]);
        }
      }
    );
  };

  return (
    <form onSubmit={formik.handleSubmit} style={{ width: "100%" }}>
      <FormikProvider value={formik}>
        {configuration && (
          <HStack w="100%" pb={4}>
            <Box flexGrow={2}>
              <Text fontSize="xl" color="cherryUi.600" fontWeight="bold">
                {configuration.FriendlyName}
              </Text>
            </Box>
            <Box>
              <Button
                colorScheme="cherryButton"
                type="submit"
                isLoading={formik.isSubmitting}
                isDisabled={formik.isSubmitting || !formik.isValid}
              >
                Save
              </Button>
            </Box>
            <Box>
              <Button isDisabled={formik.isSubmitting} onClick={onCancel}>
                Cancel
              </Button>
            </Box>
          </HStack>
        )}

        <Tabs variant="edit" w="100%" isLazy>
          <TabList>
            <Tab>Setup</Tab>
            <Tab>Page Content</Tab>
            <Tab>Fields</Tab>
          </TabList>

          <TabPanels>
            {/* Tab 1: Setup */}
            <TabPanel>
              <VStack
                w="100%"
                maxW="1200px"
                alignItems="flex-start"
                pb={6}
                spacing={4}
              >
                <TextField
                  horizontal
                  label="Friendly Name"
                  name="friendlyName"
                />
                <SwitchField horizontal label="Enabled" name="isEnabled" />
                <ChakraReactSelectField
                  horizontal
                  label="Integration"
                  name="integrationId"
                  selectedOptionColor="blue"
                  isLoading={integrationsLoading}
                  allowEmpty="No Integration"
                  options={integrationOptions}
                  onChange={(val) => {
                    const selected = val as SingleValue<SelectOption>;
                    formik.setFieldValue(
                      "integrationId",
                      selected?.value ?? null
                    );
                    formik.setFieldValue("formFields", []);
                  }}
                />
                <TextField
                  horizontal
                  label="Language"
                  name="language"
                  helperText="BCP-47 code e.g. en-AU"
                />
                <ChakraReactSelectField
                  horizontal
                  label="OTP Method"
                  name="otpMethod"
                  selectedOptionColor="blue"
                  options={OTP_METHOD_OPTIONS}
                />
                <SwitchField
                  horizontal
                  label="Return Preferred OTP Method Only"
                  name="otpReturnPreferredMethodOnly"
                />
                <Box
                  w="100%"
                  p={4}
                  border="1px solid"
                  borderColor="cherryButtonDark.50"
                  borderRadius="md"
                >
                  <Text fontSize="sm" mb={2}>
                    <b>OTP Valid Time Period</b>
                  </Text>
                  <Divider mb={2} />
                  <HStack w="100%" spacing={4} alignItems="flex-start">
                    <IntegerField
                      horizontal
                      label="Quantity"
                      name="otpValidTimePeriodQuantity"
                    />
                    <ChakraReactSelectField
                      horizontal
                      label="Period"
                      name="otpValidTimePeriodUnit"
                      selectedOptionColor="blue"
                      options={TIME_UNIT_OPTIONS}
                    />
                  </HStack>
                </Box>
              </VStack>
            </TabPanel>

            {/* Tab 2: Page Content */}
            <TabPanel>
              <VStack
                w="100%"
                maxW="1200px"
                alignItems="flex-start"
                pb={6}
                spacing={4}
              >
                <TextField horizontal label="Page Title" name="pageTitle" />
                <TextField horizontal label="Logo URL" name="logoFileUrl" />
                <HtmlTextArea
                  horizontal
                  label="Header Text"
                  name="headerText"
                />
                <HtmlTextArea
                  horizontal
                  label="Footer Text"
                  name="footerText"
                />
                <TextField
                  horizontal
                  label="Privacy Policy URL"
                  name="privacyPolicyUrl"
                />
                <TextField
                  horizontal
                  label="Terms & Conditions URL"
                  name="termsConditionsUrl"
                />
                <ChakraReactSelectField
                  horizontal
                  label="Geo Country Filter"
                  name="geoSuggestCountryFilter"
                  isMulti
                  creatable
                  selectedOptionColor="blue"
                  options={[]}
                  helperText="Type country codes and press enter"
                  keepSelectionOrderInOptions
                />
              </VStack>
            </TabPanel>

            {/* Tab 3: Fields */}
            <TabPanel>
              <VStack
                w="100%"
                maxW="1200px"
                alignItems="flex-start"
                pb={6}
                spacing={4}
              >
                {!selectedIntegrationId && (
                  <Text color="gray.500" fontSize="sm">
                    Select an integration on the Setup tab to load available
                    fields.
                  </Text>
                )}
                {selectedIntegrationId && formFieldsLoading && (
                  <Text color="gray.500" fontSize="sm">
                    Loading fields...
                  </Text>
                )}

                {selectedIntegrationId && !formFieldsLoading && (
                  <>
                    <Button
                      size="sm"
                      colorScheme="cherryButton"
                      onClick={addFields}
                      isDisabled={formFieldsLoading}
                    >
                      Add Fields
                    </Button>

                    <DndContext
                      sensors={sensors}
                      collisionDetection={rectIntersection}
                      onDragStart={handleDragStart}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext
                        items={formik.values.formFields.map((f) =>
                          getFormFieldId(f)
                        )}
                        strategy={verticalListSortingStrategy}
                      >
                        <VStack w="100%" spacing={2} alignItems="flex-start">
                          {formik.values.formFields.map((field, index) => (
                            <SortableItem
                              key={getFormFieldId(field)}
                              id={getFormFieldId(field)}
                              w="100%"
                              p={2}
                              border="1px solid"
                              borderColor="gray.200"
                              borderRadius="md"
                            >
                              <VStack
                                flex={1}
                                alignItems="flex-start"
                                spacing={2}
                              >
                                <Tag size="sm">{field.Identifier}</Tag>
                                <TextField
                                  name={`formFields[${index}].Name`}
                                  label="Label"
                                  horizontal
                                />
                                <SwitchField
                                  horizontal
                                  label="Verification Required"
                                  name={`formFields[${index}].VerificationRequired`}
                                  colorScheme="green"
                                />
                                <SwitchField
                                  horizontal
                                  label="Obfuscated"
                                  name={`formFields[${index}].IsObfuscated`}
                                  colorScheme="green"
                                />
                              </VStack>
                              <IconButton
                                size="xs"
                                alignSelf="flex-start"
                                icon={<CloseIcon />}
                                aria-label="Remove"
                                title="Remove"
                                borderRadius="full"
                                onClick={() =>
                                  formik.setFieldValue(
                                    "formFields",
                                    formik.values.formFields.filter(
                                      (_, i) => i !== index
                                    )
                                  )
                                }
                              />
                            </SortableItem>
                          ))}
                        </VStack>
                      </SortableContext>

                      <DragOverlay>
                        {activeFieldId
                          ? (() => {
                              const f = formik.values.formFields.find(
                                (f) => getFormFieldId(f) === activeFieldId
                              );
                              return f ? (
                                <SortableItem
                                  id={activeFieldId}
                                  w="100%"
                                  p={2}
                                  border="1px solid"
                                  borderColor="gray.200"
                                  borderRadius="md"
                                  bg="white"
                                >
                                  <VStack
                                    flex={1}
                                    alignItems="flex-start"
                                    spacing={2}
                                  >
                                    <Tag size="sm">{f.Identifier}</Tag>
                                    <Text fontSize="sm">{f.Name}</Text>
                                  </VStack>
                                </SortableItem>
                              ) : null;
                            })()
                          : null}
                      </DragOverlay>
                    </DndContext>

                    {customFieldModalElement}

                    <Accordion w="100%" allowToggle variant="section">
                      <AccordionItem>
                        <>
                          <h2>
                            <AccordionButton>
                              <Box flex="1" textAlign="left">
                                <b>Advanced</b>
                              </Box>
                              <AccordionIcon />
                            </AccordionButton>
                          </h2>
                          <AccordionPanel pb={4}>
                            <JsonTextAreaField
                              horizontal
                              label="Theme Data"
                              name="themeData"
                              valueText={formik.values.themeData}
                              errors={formik.errors.themeData}
                              setFieldValue={formik.setFieldValue}
                              rows={10}
                            />
                          </AccordionPanel>
                        </>
                      </AccordionItem>
                    </Accordion>
                  </>
                )}

                {!selectedIntegrationId && (
                  <Accordion w="100%" allowToggle variant="section">
                    <AccordionItem>
                      <>
                        <h2>
                          <AccordionButton>
                            <Box flex="1" textAlign="left">
                              <b>Advanced</b>
                            </Box>
                            <AccordionIcon />
                          </AccordionButton>
                        </h2>
                        <AccordionPanel pb={4}>
                          <JsonTextAreaField
                            horizontal
                            label="Theme Data"
                            name="themeData"
                            valueText={formik.values.themeData}
                            errors={formik.errors.themeData}
                            setFieldValue={formik.setFieldValue}
                            rows={10}
                          />
                        </AccordionPanel>
                      </>
                    </AccordionItem>
                  </Accordion>
                )}
              </VStack>
            </TabPanel>
          </TabPanels>
        </Tabs>

        <HStack width="100%" justifyContent="center" spacing="3" pt="8">
          <Button
            type="submit"
            colorScheme="cherryButton"
            isDisabled={formik.isSubmitting || !formik.isValid}
          >
            Save
          </Button>
          <Button isDisabled={formik.isSubmitting} onClick={onCancel}>
            Cancel
          </Button>
        </HStack>
      </FormikProvider>
    </form>
  );
};
